<template>
    <v-container fluid style="max-width: 720px">
        <h2 class="text-h6 mb-1">Azienda</h2>
        <div class="text-body-2 text-medium-emphasis mb-4">Dati anagrafici e logo della tua azienda</div>

        <v-card class="mb-4" variant="outlined">
            <v-card-title>Logo</v-card-title>
            <v-card-text>
                <div class="d-flex align-center" style="gap: 20px">
                    <v-avatar size="80" rounded="lg" color="grey-lighten-3">
                        <v-img v-if="logoUrl" :src="logoUrl" cover />
                        <v-icon v-else size="36" color="grey">mdi-domain</v-icon>
                    </v-avatar>
                    <div class="flex-grow-1">
                        <v-file-input v-model="fileLogo" label="Carica logo (jpg, png, webp)" accept="image/*"
                            density="compact" variant="outlined" hide-details prepend-icon="mdi-image"
                            :loading="caricandoLogo" @update:model-value="caricaLogo" />
                        <v-btn v-if="logoUrl" size="small" variant="text" color="error" class="mt-2"
                            :loading="rimuovendoLogo" @click="rimuoviLogo">
                            Rimuovi logo
                        </v-btn>
                    </div>
                </div>
                <div class="text-caption text-medium-emphasis mt-2">
                    Visibile a tutti gli utenti della tua azienda, in fondo al menu laterale.
                </div>
            </v-card-text>
        </v-card>

        <v-card variant="outlined">
            <v-card-title>Dati aziendali</v-card-title>
            <v-card-text>
                <v-form ref="formRef">
                    <v-text-field v-model="form.ragioneSociale" label="Ragione sociale" class="mb-4"
                        :rules="[v => !!v || 'Obbligatorio']" />
                    <v-text-field v-model="form.partitaIva" label="Partita IVA" class="mb-4" />
                    <v-row>
                        <v-col cols="12" sm="8"><v-text-field v-model="form.indirizzo"
                                label="Indirizzo (via/piazza e civico)" /></v-col>
                        <v-col cols="12" sm="4"><v-text-field v-model="form.localita" label="Località"
                                hint="Frazione, se presente" /></v-col>
                    </v-row>
                    <v-row class="mb-2">
                        <v-col cols="4" sm="3"><v-text-field v-model="form.cap" label="CAP" /></v-col>
                        <v-col cols="8" sm="6"><v-text-field v-model="form.comune" label="Comune" /></v-col>
                        <v-col cols="4" sm="3"><v-text-field v-model="form.provincia" label="Provincia"
                                maxlength="2" /></v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.email" label="Email" type="email" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.telefono" label="Telefono" />
                        </v-col>
                    </v-row>
                </v-form>

                <v-divider class="my-4" />

                <div class="text-caption text-medium-emphasis mb-1">Pianificazioni ricorrenti</div>
                <v-text-field v-model.number="anniRicorrenza" type="number" label="Anni di ricorrenza pianificazioni"
                    density="compact" style="max-width: 320px"
                    hint="Orizzonte di generazione delle occorrenze future per manutenzioni, scadenze mezzo, pagamenti e incassi con la spunta 'Ricorrente' attiva."
                    persistent-hint :rules="[v => v > 0 || 'Deve essere maggiore di zero']" />

                <v-divider class="my-4" />

                <div class="text-caption text-medium-emphasis mb-1">Piano e abbonamento (gestiti dal gestore del servizio)</div>
                <div class="d-flex align-center" style="gap: 12px">
                    <v-chip size="small" variant="tonal">{{ dati?.piano || '—' }}</v-chip>
                    <v-chip size="small" :color="dati?.attivo ? 'success' : 'error'" variant="flat">
                        {{ dati?.attivo ? 'Attivo' : 'Disattivato' }}
                    </v-chip>
                    <span v-if="dati?.dataScadenza" class="text-caption text-medium-emphasis">
                        Scadenza: {{ formattaData(dati.dataScadenza) }}
                    </span>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-alert v-if="messaggio" type="success" density="compact" variant="tonal" class="mr-2 py-1">
                    {{ messaggio }}
                </v-alert>
                <v-btn color="primary" :loading="salvando" @click="salva">Salva</v-btn>
            </v-card-actions>
        </v-card>
    </v-container>
</template>

<script>
import { get_parametri_azienda, salva_parametri_azienda } from "@/api/api.parametriAzienda.js";

export default {
    name: "MiaAziendaPage",

    data() {
        return {
            form: {
                ragioneSociale: "", partitaIva: "",
                indirizzo: "", localita: "", cap: "", comune: "", provincia: "",
                email: "", telefono: "",
            },
            anniRicorrenza: 10,
            salvando: false,
            messaggio: "",
            fileLogo: null,
            caricandoLogo: false,
            rimuovendoLogo: false,
        };
    },

    computed: {
        dati() {
            return this.$store.getters["miaAzienda/dati"];
        },
        logoUrl() {
            return this.$store.getters["miaAzienda/logoUrl"];
        },
    },

    async mounted() {
        try {
            await this.$store.dispatch("miaAzienda/carica");
            await this.$store.dispatch("miaAzienda/caricaLogo");
            const resp = await get_parametri_azienda();
            this.anniRicorrenza = resp.data.anniRicorrenza;
        } catch (error) {
            console.log(error);
        }
        if (this.dati) {
            this.form = {
                ragioneSociale: this.dati.ragioneSociale,
                partitaIva: this.dati.partitaIva,
                indirizzo: this.dati.indirizzo,
                localita: this.dati.localita,
                cap: this.dati.cap,
                comune: this.dati.comune,
                provincia: this.dati.provincia,
                email: this.dati.email,
                telefono: this.dati.telefono,
            };
        }
    },

    methods: {
        formattaData(iso) {
            return new Date(iso).toLocaleDateString("it-IT");
        },

        async salva() {
            if (!this.$refs.formRef.validate()) return;
            this.messaggio = "";
            this.salvando = true;
            try {
                await this.$store.dispatch("miaAzienda/salva", { ...this.form });
                await salva_parametri_azienda({ anniRicorrenza: this.anniRicorrenza });
                this.messaggio = "Dati aggiornati.";
            } catch (error) {
                console.log(error);
            }
            this.salvando = false;
        },

        async caricaLogo(file) {
            if (!file) return;
            this.caricandoLogo = true;
            try {
                await this.$store.dispatch("miaAzienda/uploadLogo", file);
            } catch (error) {
                console.log(error);
            }
            this.fileLogo = null;
            this.caricandoLogo = false;
        },

        async rimuoviLogo() {
            this.rimuovendoLogo = true;
            try {
                await this.$store.dispatch("miaAzienda/eliminaLogo");
            } catch (error) {
                console.log(error);
            }
            this.rimuovendoLogo = false;
        },
    },
};
</script>
